import { useState } from "react";
import { sortDiagnostics, validateFlowSchema, type Diagnostic } from "./validateFlow";
import { setNodeConfig } from "./nodeConfig";
import { setEdgePort } from "./edgeGraph";
import { checkMissingPorts, checkUnknownPorts } from "./requiredPorts";
import { adviseFlow, type Advice } from "./flowAdvice";
import { checkDuplicatePort, checkUnboundedLoop } from "./cycles";
import {
  configFromDraft,
  draftFromConfig,
  emptyPrompt,
  emptyRow,
  TYPED_NODE_FIELDS,
  type Draft,
  type FieldDef,
  type FieldValue,
  type PromptDraft,
  type RowDraft,
} from "./nodeConfigForm";
import type { FlowDocument, FlowEdge, FlowNode } from "./types";

interface InspectorProps {
  document: FlowDocument;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  onChange: (document: FlowDocument) => void;
  onRemoveNode: (nodeId: string) => void;
  onRemoveEdge: (edgeId: string) => void;
}

/**
 * Views and edits whichever single thing is currently selected on the
 * canvas — one node's `config`, or one edge's own port name — and owns
 * the matching "Delete" action for each (removeNode/removeEdge,
 * nodeGraph.ts/edgeGraph.ts). A node and an edge are never selected at
 * the same time (FlowCanvas's own click handlers clear the other), so
 * this only ever renders one editor.
 *
 * A node's config uses a typed form (nodeConfigForm.ts) for every node
 * type the schema declares (Slices AI–AL): the six flat-shape ones (play,
 * tts, hangup, transfer, consent, tag), repeat/http/branch (a `rows` kind
 * for their own array-shaped field), and gather (a `prompt` kind for its
 * nested audio/tts oneOf). Only `start` and `amd_gate` still get the raw
 * JSON textarea, because they have no real config to edit — `start`'s is
 * always `{}`, and amd_gate is excluded from the palette entirely.
 *
 * The diagnostics panel below checks schema-shape (always did) plus four
 * real semantic checks mirroring Go's own `semantic.go`/`graph.go`: missing
 * required ports (Slice AM), an edge's port name outside its source node's
 * allowed set, a duplicate source+port edge, and an unbounded loop with no
 * Repeat controller (all three new, Slice BY) — still not full parity with
 * Go's semantic layer as a whole: no reachability/variable/AMD-placement
 * checks here, and no edge-from-hangup check (checkUnknownPorts's own doc
 * comment in requiredPorts.ts explains why that one specific case is
 * skipped rather than misreported).
 *
 * Deleting the document's own start node is disabled — it can never be
 * removed and leave a valid document — but deleting an edge is always
 * allowed; no edge is structurally required to exist. Changing a node's
 * `type` is out of scope here too: that would change its whole required
 * config shape, a bigger editing problem this slice does not solve.
 */
export function Inspector({
  document,
  selectedNodeId,
  selectedEdgeId,
  onChange,
  onRemoveNode,
  onRemoveEdge,
}: InspectorProps) {
  const selectedNode = document.nodes.find((node) => node.id === selectedNodeId) ?? null;
  const selectedEdge = document.edges.find((edge) => edge.id === selectedEdgeId) ?? null;

  // Computed before the early return below, and rendered in both branches:
  // advice is about the whole document, not the selection, and the moment a
  // reader most needs it is while nothing is selected. The diagnostics panel
  // predates this and stays selection-bound.
  const advice = adviseFlow(document);

  if (!selectedNode && !selectedEdge) {
    return (
      <aside className="card inspector" aria-labelledby="inspector-heading">
        <h2 id="inspector-heading">Inspector</h2>
        <p className="card__note">Select a node or edge on the canvas to view and edit it.</p>
        <FlowAdviceList advice={advice} />
      </aside>
    );
  }

  const diagnostics: Diagnostic[] = sortDiagnostics([
    ...validateFlowSchema(document),
    ...checkMissingPorts(document),
    ...checkUnknownPorts(document),
    ...checkDuplicatePort(document),
    ...checkUnboundedLoop(document),
  ]);

  return (
    <aside className="card inspector" aria-labelledby="inspector-heading">
      <h2 id="inspector-heading">Inspector</h2>
      {selectedEdge ? (
        <>
          {/* Keyed by edge id: remounting on selection change resets the
              draft port text to the newly selected edge's own value. */}
          <EdgePortEditor
            key={selectedEdge.id}
            edge={selectedEdge}
            onApply={(port) => onChange(setEdgePort(document, selectedEdge.id, port))}
          />
          <button type="button" className="inspector__delete" onClick={() => onRemoveEdge(selectedEdge.id)}>
            Delete edge
          </button>
        </>
      ) : selectedNode ? (
        <>
          {/* Keyed by node id, not just rendered with props: remounting on
              selection change is what resets the draft/parse-error state below
              to the newly selected node's own config, with no effect needed. */}
          <NodeConfigEditor
            key={selectedNode.id}
            node={selectedNode}
            onApply={(config) => onChange(setNodeConfig(document, selectedNode.id, config))}
          />
          <button
            type="button"
            className="inspector__delete"
            disabled={selectedNode.id === document.start_node_id}
            title={selectedNode.id === document.start_node_id ? "The start node cannot be removed" : undefined}
            onClick={() => onRemoveNode(selectedNode.id)}
          >
            Delete node
          </button>
        </>
      ) : null}
      {diagnostics.length > 0 ? (
        <div className="inspector__diagnostics">
          <h3>Schema diagnostics ({diagnostics.length})</h3>
          <ul>
            {diagnostics.map((diagnostic, index) => (
              <li key={index}>
                <code>{diagnostic.path}</code> — {diagnostic.detail}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="card__note">Document passes schema validation.</p>
      )}
      <FlowAdviceList advice={advice} />
    </aside>
  );
}

/**
 * Advice about what a caller will experience, kept visually and verbally
 * separate from the schema diagnostics above it.
 *
 * The separation is the point: everything in the diagnostics list stops a
 * publish, and nothing here does. Presenting them as one list would teach a
 * flow author that this panel's warnings can be ignored, which is exactly
 * wrong for the half of it that cannot.
 */
function FlowAdviceList({ advice }: { advice: Advice[] }) {
  if (advice.length === 0) return null;
  return (
    <div className="inspector__advice" role="note">
      <h3>On a real call ({advice.length})</h3>
      <ul>
        {advice.map((item) => (
          <li key={`${item.code}:${item.nodeId}:${item.detail}`}>{item.detail}</li>
        ))}
      </ul>
      <p className="card__note">
        Valid flow — these publish and dial. They are places a caller is likely to think
        something went wrong.
      </p>
    </div>
  );
}

interface NodeConfigEditorProps {
  node: FlowNode;
  onApply: (config: unknown) => void;
}

function NodeConfigEditor({ node, onApply }: NodeConfigEditorProps) {
  const fields = TYPED_NODE_FIELDS[node.type];
  if (fields) {
    return <TypedNodeConfigForm node={node} fields={fields} onApply={onApply} />;
  }
  return <RawNodeConfigEditor node={node} onApply={onApply} />;
}

function NodeIdentity({ node }: { node: FlowNode }) {
  return (
    <dl className="inspector__meta">
      <div>
        <dt>Node id</dt>
        <dd>{node.id}</dd>
      </div>
      <div>
        <dt>Type</dt>
        <dd>{node.type}</dd>
      </div>
    </dl>
  );
}

function textValue(value: FieldValue | undefined): string {
  return typeof value === "string" ? value : "";
}

function rowsValue(value: FieldValue | undefined): RowDraft[] {
  return Array.isArray(value) ? value : [];
}

function promptValue(value: FieldValue | undefined): PromptDraft {
  return value && typeof value === "object" && !Array.isArray(value) ? value : emptyPrompt();
}

function TypedNodeConfigForm({
  node,
  fields,
  onApply,
}: {
  node: FlowNode;
  fields: FieldDef[];
  onApply: (config: unknown) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFromConfig(fields, node.config));

  function setField(key: string, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function addRow(field: FieldDef) {
    setDraft((current) => ({ ...current, [field.key]: [...rowsValue(current[field.key]), emptyRow(field)] }));
  }

  function removeRow(field: FieldDef, index: number) {
    setDraft((current) => ({
      ...current,
      [field.key]: rowsValue(current[field.key]).filter((_, rowIndex) => rowIndex !== index),
    }));
  }

  function setRowField(field: FieldDef, index: number, rowKey: string, value: string) {
    setDraft((current) => ({
      ...current,
      [field.key]: rowsValue(current[field.key]).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [rowKey]: value } : row,
      ),
    }));
  }

  function setPromptField(field: FieldDef, key: "asset_id" | "text" | "voice" | "language", value: string) {
    setDraft((current) => ({ ...current, [field.key]: { ...promptValue(current[field.key]), [key]: value } }));
  }

  function setPromptKind(field: FieldDef, kind: PromptDraft["kind"]) {
    setDraft((current) => ({ ...current, [field.key]: { ...promptValue(current[field.key]), kind } }));
  }

  return (
    <>
      <NodeIdentity node={node} />
      {fields.map((field) => {
        const inputId = `inspector-field-${field.key}`;
        return (
          <div className="inspector__field" key={field.key}>
            <label htmlFor={field.kind === "rows" || field.kind === "prompt" ? undefined : inputId}>
              {field.label}
            </label>
            {field.kind === "select" ? (
              <select
                id={inputId}
                className="inspector__port"
                value={textValue(draft[field.key])}
                onChange={(event) => setField(field.key, event.target.value)}
              >
                {(field.options ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.kind === "textarea" ? (
              <textarea
                id={inputId}
                className="inspector__config"
                rows={4}
                value={textValue(draft[field.key])}
                onChange={(event) => setField(field.key, event.target.value)}
                spellCheck={false}
              />
            ) : field.kind === "rows" ? (
              <div className="inspector__rows">
                {rowsValue(draft[field.key]).map((row, index) => (
                  // Rows have no stable id of their own; index is fine since
                  // add/remove always rebuild the array from this same draft.
                  <div className="inspector__row" key={index}>
                    {(field.rowFields ?? []).map((rowField) =>
                      rowField.kind === "select" ? (
                        <select
                          key={rowField.key}
                          aria-label={rowField.label}
                          className="inspector__port"
                          value={row[rowField.key] ?? ""}
                          onChange={(event) => setRowField(field, index, rowField.key, event.target.value)}
                        >
                          {(rowField.options ?? []).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          key={rowField.key}
                          aria-label={rowField.label}
                          className="inspector__port"
                          type={rowField.kind === "number" ? "number" : "text"}
                          value={row[rowField.key] ?? ""}
                          onChange={(event) => setRowField(field, index, rowField.key, event.target.value)}
                          spellCheck={false}
                        />
                      ),
                    )}
                    <button type="button" className="inspector__delete" onClick={() => removeRow(field, index)}>
                      Remove row
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addRow(field)}>
                  Add row
                </button>
              </div>
            ) : field.kind === "prompt" ? (
              <PromptEditor
                prompt={promptValue(draft[field.key])}
                onKindChange={(kind) => setPromptKind(field, kind)}
                onFieldChange={(key, value) => setPromptField(field, key, value)}
              />
            ) : (
              <input
                id={inputId}
                className="inspector__port"
                type={field.kind === "number" ? "number" : "text"}
                value={textValue(draft[field.key])}
                onChange={(event) => setField(field.key, event.target.value)}
                spellCheck={false}
              />
            )}
          </div>
        );
      })}
      <button type="button" onClick={() => onApply(configFromDraft(fields, draft))}>
        Apply
      </button>
    </>
  );
}

function PromptEditor({
  prompt,
  onKindChange,
  onFieldChange,
}: {
  prompt: PromptDraft;
  onKindChange: (kind: PromptDraft["kind"]) => void;
  onFieldChange: (key: "asset_id" | "text" | "voice" | "language", value: string) => void;
}) {
  return (
    <div className="inspector__prompt">
      <select
        aria-label="Prompt kind"
        className="inspector__port"
        value={prompt.kind}
        onChange={(event) => onKindChange(event.target.value === "tts" ? "tts" : "audio")}
      >
        <option value="audio">audio</option>
        <option value="tts">tts</option>
      </select>
      {prompt.kind === "tts" ? (
        <>
          <textarea
            aria-label="Prompt text"
            className="inspector__config"
            rows={3}
            value={prompt.text}
            onChange={(event) => onFieldChange("text", event.target.value)}
            spellCheck={false}
          />
          <input
            aria-label="Prompt voice"
            className="inspector__port"
            type="text"
            value={prompt.voice}
            onChange={(event) => onFieldChange("voice", event.target.value)}
            spellCheck={false}
          />
          <input
            aria-label="Prompt language"
            className="inspector__port"
            type="text"
            value={prompt.language}
            onChange={(event) => onFieldChange("language", event.target.value)}
            spellCheck={false}
          />
        </>
      ) : (
        <input
          aria-label="Prompt asset ID"
          className="inspector__port"
          type="text"
          value={prompt.asset_id}
          onChange={(event) => onFieldChange("asset_id", event.target.value)}
          spellCheck={false}
        />
      )}
    </div>
  );
}

function RawNodeConfigEditor({ node, onApply }: NodeConfigEditorProps) {
  const [draft, setDraft] = useState(() => JSON.stringify(node.config, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);

  function apply() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "invalid JSON");
      return;
    }
    setParseError(null);
    onApply(parsed);
  }

  return (
    <>
      <NodeIdentity node={node} />
      <label htmlFor="inspector-config">Config (JSON)</label>
      <textarea
        id="inspector-config"
        className="inspector__config"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        rows={10}
        spellCheck={false}
      />
      {parseError ? <p className="card__error">Invalid JSON: {parseError}</p> : null}
      <button type="button" onClick={apply}>
        Apply
      </button>
    </>
  );
}

interface EdgePortEditorProps {
  edge: FlowEdge;
  onApply: (port: string) => void;
}

function EdgePortEditor({ edge, onApply }: EdgePortEditorProps) {
  const [draft, setDraft] = useState(edge.port);

  return (
    <>
      <dl className="inspector__meta">
        <div>
          <dt>Source</dt>
          <dd>{edge.source}</dd>
        </div>
        <div>
          <dt>Target</dt>
          <dd>{edge.target}</dd>
        </div>
      </dl>
      <label htmlFor="inspector-port">Port</label>
      <input
        id="inspector-port"
        className="inspector__port"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        spellCheck={false}
      />
      <button type="button" onClick={() => onApply(draft)}>
        Apply
      </button>
    </>
  );
}
